"use client";

import { useState, type FormEvent, type KeyboardEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search as SearchIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DEFAULT_GALLERY_SORT, withGallerySortParam } from "@/lib/gallerySort";
import {
  normalizeGallerySearch,
  withGallerySearchParam,
} from "@/lib/gallerySearch";
import type { GallerySort } from "@/types/gallery";

interface GallerySearchFormProps {
  action: string;
  clearSearchHref: string;
  search: string;
  sort: GallerySort;
}

export default function GallerySearchForm({
  action,
  clearSearchHref,
  search,
  sort,
}: GallerySearchFormProps) {
  const router = useRouter();
  // Seeded from the URL; the parent keys this form by `search`, so a new
  // search navigation remounts it with the new value.
  const [query, setQuery] = useState(search);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const query = normalizeGallerySearch(String(formData.get("q") ?? ""));
    const href = withGallerySearchParam(
      withGallerySortParam(action, sort),
      query
    );

    router.push(href);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;

    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  };

  return (
    <form
      action={action}
      method="get"
      role="search"
      className="flex min-w-0 w-full items-center gap-2"
      onSubmit={handleSubmit}
    >
      <label htmlFor="gallery-search" className="sr-only">
        Search dress designs
      </label>
      <div className="relative min-w-0 flex-1">
        <SearchIcon
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          id="gallery-search"
          name="q"
          type="search"
          value={query}
          onChange={event => setQuery(event.currentTarget.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search style, color, or fabric"
          className="h-11 w-full rounded-lg border border-border bg-card pl-10 pr-12 text-base md:text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
        {search ? (
          <Link
            href={clearSearchHref}
            aria-label="Clear gallery search"
            className="absolute right-0 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </Link>
        ) : null}
      </div>
      {sort !== DEFAULT_GALLERY_SORT ? (
        <input type="hidden" name="sort" value={sort} />
      ) : null}
      <Button type="submit" className="h-11">
        Search
      </Button>
    </form>
  );
}
